import { useEffect, useMemo, useRef, useState } from 'react';
import ControlObraService from 'src/services/controlObra/controlObraService';
import { mensajeDeError } from 'src/utils/mensajeDeError';

/**
 * Lo que el copiloto tiene para proponer, agrupado por TEMA.
 *
 * Por tema y no por fila: en una obra de 129 tareas, "quién la ejecuta" falta 76
 * veces. Preguntarlo de a una no es una pregunta por vez —es hacerle contestar
 * lo mismo 76 veces, y a la tercera el copiloto se apaga—. El apalancamiento
 * está en ver el patrón, que es justo lo que la persona no puede ver sola.
 *
 * Vive en un hook y no adentro de la tarjeta porque el input del panel es uno
 * solo: para poder contestarle al tema escribiendo, quien tiene el input tiene
 * que poder llegar al tema abierto.
 */

// Lo que no se dedujo de un dato duro: sale de otras obras o de un modelo.
export const ESTIMADO = ['ia', 'obra_previa'];

export const ETIQUETA_CAMPO = {
  monto: 'el costo',
  fecha_fin: 'la fecha de fin',
  ejecucion: 'quién la ejecuta',
};

const plural = (n, uno, muchos) => (n === 1 ? uno : muchos);

/**
 * Cómo se cuenta cada tema. La clave es qué pregunta es, no qué campo se
 * escribe: el costo que sale de un contrato firmado y el que sale de estimar
 * contra obras anteriores se escriben en el mismo lugar y no son la misma
 * conversación.
 */
const TEMAS = {
  ejecucion_sin_proveedor: {
    titulo: (n) => `${n} ${plural(n, 'tarea no dice', 'tareas no dicen')} quién ${plural(n, 'la hace', 'las hace')}`,
    detalle: (n) => `${plural(n, 'Tiene', 'Todas tienen')} costo cargado y ${plural(n, 'no tiene', 'ninguna tiene')} proveedor asignado.`,
    pregunta: (n) => `¿${plural(n, 'La hacen', 'Las hacen')} con gente propia?`,
    enPocas: 'con gente propia',
    aceptar: (n) => `Sí, gente propia${n > 1 ? ` (${n})` : ''}`,
  },
  costo_desde_contrato: {
    titulo: (n) => `${n} ${plural(n, 'tarea contratada', 'tareas contratadas')} sin costo cargado`,
    detalle: (n) => `Ya ${plural(n, 'tiene', 'tienen')} precio acordado con el proveedor. Ese es el costo.`,
    pregunta: () => null,
    enPocas: 'con el precio acordado con el proveedor',
    aceptar: (n) => `Usar el precio acordado${n > 1 ? ` (${n})` : ''}`,
  },
  costo_por_analogia: {
    titulo: (n) => `${n} ${plural(n, 'tarea', 'tareas')} sin costo`,
    detalle: () => 'Te lo puedo estimar con lo que costaron en tus obras anteriores.',
    pregunta: () => null,
    enPocas: 'con el costo estimado',
    aceptar: (n) => `Usar la estimación${n > 1 ? ` (${n})` : ''}`,
  },
  costo_por_margen: {
    titulo: (n) => `${n} ${plural(n, 'tarea', 'tareas')} sin costo, repartidas para el margen que pediste`,
    detalle: () => 'Es lo que tendría que costar cada una para que la obra cierre en ese margen. No toca lo ya contratado.',
    pregunta: () => null,
    enPocas: 'con el costo del margen objetivo',
    aceptar: (n) => `Usar estos costos${n > 1 ? ` (${n})` : ''}`,
  },
  /**
   * El único tema que no propone cargar un dato: propone sacar de la vista una
   * capa que hace meses no se toca. Por eso no tiene filas que recorrer —cada
   * ítem es un interruptor de la obra, no una tarea— y por eso el "no" dice que
   * la vas a usar, que es la respuesta real a esta pregunta.
   */
  capa_sin_uso: {
    titulo: (n) => `${n} ${plural(n, 'cosa que no usás', 'cosas que no usás')} en esta obra`,
    detalle: () => 'Sacarlas de la vista deja de reclamártelas y sube el nivel de la obra. No se borra nada: se vuelven a prender desde Ajustes.',
    pregunta: () => null,
    enPocas: 'fuera de la vista',
    aceptar: (n) => (n > 1 ? `Sacarlas de la vista (${n})` : 'Sacarlo de la vista'),
    negar: (n) => (n > 1 ? 'Las voy a usar' : 'Lo voy a usar'),
    sinFilas: true,
  },
  /**
   * El único tema del régimen PROFUNDIZAR — D11.
   *
   * No pide un dato que se debía: ofrece medir mejor. Por eso no tiene botón de
   * aceptar —el sistema no sabe cuánto pesa cada tarea, si lo supiera no habría
   * nada que ofrecer— y por eso el "no" no es "no sé": es "así está bien", que
   * es una respuesta legítima a una oferta.
   */
  peso_parejo: {
    titulo: () => 'Este número puede decir más de lo que dice',
    // Lo específico lo dice la propuesta: qué rubro, cuántas tareas, cuánta plata.
    detalle: (_n, tema) => tema?.items?.[0]?.fundamento || null,
    pregunta: () => null,
    enPocas: 'medido de verdad',
    aceptar: () => null,
    negar: () => 'Así está bien',
    sinFilas: true,
    sinAceptar: true,
  },
  fin_desde_duracion: {
    titulo: (n) => `${n} ${plural(n, 'tarea', 'tareas')} sin fecha de fin`,
    detalle: (n) => `${plural(n, 'Tiene', 'Tienen')} inicio y duración cargados: el fin es una cuenta.`,
    pregunta: () => null,
    enPocas: 'con el fin calculado',
    aceptar: (n) => `Calcular el fin${n > 1 ? ` (${n})` : ''}`,
  },
};

const temaGenerico = (campo) => ({
  titulo: (n) => `${n} ${plural(n, 'tarea', 'tareas')} sin ${ETIQUETA_CAMPO[campo] || campo}`,
  detalle: () => null,
  pregunta: () => null,
  enPocas: 'les corresponde',
  aceptar: (n) => `Aceptar${n > 1 ? ` (${n})` : ''}`,
});

export default function useTemasDelCopiloto({ obra, empresaId, userId, onAplicado }) {
  const [cola, setCola] = useState([]);
  const [t, setT] = useState(0);
  // null = mirando el tema; un número = mirando la fila i de ese tema.
  const [detalle, setDetalle] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [ultimoLote, setUltimoLote] = useState(null);
  const [aplicadas, setAplicadas] = useState(0);
  // Lo que el copiloto entendió de una respuesta en prosa, esperando el sí.
  const [reparto, setReparto] = useState(null);
  // Cuando el copiloto propone algo desde el chat, ese tema pasa a ser el que se
  // mira: es la respuesta a lo que la persona acaba de pedir.
  const [claveFoco, setClaveFoco] = useState(null);
  const clavesRef = useRef(new Set());
  const [error, setError] = useState(null);

  const temas = useMemo(() => {
    const mapa = new Map();
    for (const p of cola) {
      // `tema` es explícito desde que dos generadores coincidieron en
      // tipo·campo·origen sin ser la misma pregunta. El fallback es para las
      // propuestas que quedaron guardadas antes de que el campo existiera.
      const clave = p.tema || `${p.tipo}|${p.campo}|${p.origen}`;
      if (!mapa.has(clave)) mapa.set(clave, { clave, campo: p.campo, origen: p.origen, items: [] });
      mapa.get(clave).items.push(p);
    }
    return [...mapa.values()]
      .map((g) => ({
        ...g,
        // La plata de las tareas, no la prioridad: la prioridad lleva el peso de
        // la capa adentro y mostrarla como pesos es mentir el número.
        monto: g.items.reduce((a, p) => a + (Number(p.monto) || 0), 0),
        copy: TEMAS[g.clave] || temaGenerico(g.campo),
      }))
      .sort((a, b) => b.monto - a.monto);
  }, [cola]);

  useEffect(() => { clavesRef.current = new Set(temas.map((x) => x.clave)); }, [temas]);
  useEffect(() => {
    if (!claveFoco) return;
    const i = temas.findIndex((x) => x.clave === claveFoco);
    if (i >= 0) { setT(i); setDetalle(null); setReparto(null); }
    setClaveFoco(null);
  }, [claveFoco, temas]);

  const tema = temas[t] || null;
  // Un tema de una sola tarea no tiene patrón que mostrar: es la fila. Salvo
  // los que no hablan de tareas —`sinFilas`—, donde la fila no existe.
  const enDetalle = !!tema && !tema.copy.sinFilas && (detalle != null || tema.items.length === 1);
  const iDetalle = detalle != null ? detalle : 0;
  const actual = enDetalle ? tema.items[iDetalle] || null : null;

  const cargar = async () => {
    // El copiloto vive a nivel app: sin obra declarada, lo que había propuesto
    // para la anterior no puede sobrevivir a haberse ido de ella.
    if (!obra?._id) {
      setCola((c) => (c.length ? [] : c));
      setT(0); setDetalle(null); setReparto(null);
      setUltimoLote(null); setAplicadas(0);
      return 0;
    }
    setCargando(true); setError(null);
    try {
      const r = await ControlObraService.generarPropuestas(obra._id, empresaId);
      setUltimoLote(r?.lote_id || null);
      setAplicadas(0);
      const filas = await ControlObraService.propuestas(obra._id, empresaId) || [];
      setCola(filas);
      setT(0); setDetalle(null);
      return filas.length;
    } catch (e) {
      setError(mensajeDeError(e, 'No pude armar la lista.'));
      return 0;
    } finally { setCargando(false); }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [obra?._id]);

  /**
   * Vuelve a leer la cola sin regenerarla.
   *
   * Se llama después de cada turno del chat: si el copiloto propuso algo con una
   * tool, el tema tiene que aparecer solo. Regenerar acá sería volver a correr
   * los generadores —y la estimación por obras previas sale a la red— en cada
   * mensaje.
   */
  const refrescarCola = async () => {
    if (!obra?._id) return;
    try {
      const filas = await ControlObraService.propuestas(obra._id, empresaId) || [];
      const nueva = filas
        .map((p) => p.tema || `${p.tipo}|${p.campo}|${p.origen}`)
        .find((c) => !clavesRef.current.has(c));
      setCola(filas);
      if (nueva) setClaveFoco(nueva);
    } catch (_e) {
      // En silencio: no fue una acción de la persona, no hay a quién avisarle.
    }
  };

  /** Saca de la cola lo que ya se contestó, sin recargar. */
  const sacar = (ids) => {
    const fuera = new Set(ids);
    setCola((c) => c.filter((p) => !fuera.has(p._id)));
    setDetalle(null);
    setT(0);
    setReparto(null);
  };

  const resolverUna = async (estado, valor_final) => {
    if (!actual || trabajando) return;
    setTrabajando(true); setError(null);
    try {
      await ControlObraService.resolverPropuesta(obra._id, actual._id, {
        empresa_id: empresaId, estado, valor_final, user_id: userId,
      });
      sacar([actual._id]);
      if (estado === 'aceptada' || estado === 'corregida') {
        setAplicadas((n) => n + 1); onAplicado?.();
      }
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo aplicar.'));
    } finally { setTrabajando(false); }
  };

  const resolverTema = async (estado) => {
    if (!tema || trabajando) return;
    setTrabajando(true); setError(null);
    const ids = tema.items.map((p) => p._id);
    try {
      await ControlObraService.resolverTanda(obra._id, {
        empresa_id: empresaId, ids, estado, user_id: userId,
      });
      sacar(ids);
      if (estado === 'aceptada') { setAplicadas((n) => n + ids.length); onAplicado?.(); }
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo aplicar.'));
    } finally { setTrabajando(false); }
  };

  /**
   * Contestar el tema hablando. NO escribe: devuelve lo que el copiloto entendió
   * para que la persona lo confirme, o `es_respuesta: false` si el mensaje no
   * era para el tema y hay que mandarlo al chat.
   */
  const interpretar = async (texto) => {
    if (!tema) return { es_respuesta: false };
    setTrabajando(true); setError(null);
    try {
      const r = await ControlObraService.interpretarRespuesta(obra._id, {
        empresa_id: empresaId, ids: tema.items.map((p) => p._id), texto,
      });
      if (!r?.es_respuesta) return { es_respuesta: false };
      setReparto(r);
      return r;
    } catch (e) {
      setError(mensajeDeError(e, 'No pude interpretar la respuesta.'));
      return { es_respuesta: true, error: true };
    } finally { setTrabajando(false); }
  };

  /** El sí de la persona sobre lo que el copiloto entendió. */
  const confirmarReparto = async () => {
    if (!reparto || trabajando) return;
    setTrabajando(true); setError(null);
    try {
      if (reparto.aplica.ids.length) {
        await ControlObraService.resolverTanda(obra._id, {
          empresa_id: empresaId, ids: reparto.aplica.ids, estado: 'aceptada', user_id: userId,
        });
        setAplicadas((n) => n + reparto.aplica.ids.length);
        onAplicado?.();
      }
      if (reparto.no_aplica.ids.length) {
        await ControlObraService.resolverTanda(obra._id, {
          empresa_id: empresaId, ids: reparto.no_aplica.ids, estado: 'no_aplica', user_id: userId,
        });
      }
      // Lo dudoso sigue pendiente: la forma segura de quedar en sincronía es
      // volver a preguntar cuál es la cola, no reconstruirla desde acá.
      const filas = await ControlObraService.propuestas(obra._id, empresaId) || [];
      setCola(filas);
      setT(0); setDetalle(null); setReparto(null);
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo aplicar.'));
    } finally { setTrabajando(false); }
  };

  const deshacer = async () => {
    if (!ultimoLote) return;
    setTrabajando(true);
    try {
      await ControlObraService.deshacerPropuestas(obra._id, empresaId, ultimoLote, userId);
      setAplicadas(0);
      onAplicado?.();
      await cargar();
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo deshacer.'));
    } finally { setTrabajando(false); }
  };

  return {
    cargando, trabajando, error,
    temas, t, tema, detalle, enDetalle, iDetalle, actual,
    setT, setDetalle,
    resolverTema, resolverUna, deshacer, cargar, refrescarCola,
    reparto, interpretar, confirmarReparto, descartarReparto: () => setReparto(null),
    // Deshacer sólo tiene sentido si algo se aplicó. Ofrecerlo desde el arranque
    // es un botón que no hace nada, y eso enseña a no confiar en los botones.
    puedeDeshacer: !!ultimoLote && aplicadas > 0,
  };
}
